// fullFaceBotoxData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const allerganBotox: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "allergan-botox",
        "meta": {
            "title": "Allergan Botox Treatment in Dubai | Elite Body Home",
            "description": "Non-surgical wrinkle reduction with FDA-approved Allergan Botox. Expert treatments for facial rejuvenation, hyperhidrosis, and migraine relief in Dubai.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/allergan-botox",
            "og": {
                "title": "Allergan Botox Treatment in Dubai | Elite Body Home",
                "description": "Non-surgical wrinkle reduction with FDA-approved Allergan Botox in Dubai",
                "image": "https://www.elitebodyhome.com/images/allergan-botox-og.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Allergan Botox"],
        "hero": {
            "title": "Allergan Botox Treatment in Dubai",
            "subtitle": "Non-Surgical Facial Rejuvenation",
            "description": "At Elite Body Home, where science and beauty collide, discover the life-changing potential of Allergan Botox. Without requiring surgery, our skilled staff specializes in providing youthful, natural results that eliminate fine lines and wrinkles. You may confidently improve your appearance and regain your glowing complexion with Allergan's well-established reputation and our individualized approach. Get to know a new, self-assured you now."
        },
        "howItWorks": {
            "title": "What is Allergan Botox?",
            "description": "Allergan Botox is a trusted, FDA-approved treatment designed to smooth wrinkles and fine lines. By temporarily relaxing facial muscles, Botox minimizes the appearance of expression lines, crow's feet, and forehead wrinkles, leaving you with a rejuvenated, youthful appearance."
        },
        "procedureSteps": {
            "heading": "Steps for the Allergan Botox Treatment",
            "para": "Allergan Botox Treatment is a leading cosmetic procedure designed to reduce the appearance of fine lines and wrinkles, providing a more youthful and refreshed look. This minimally invasive procedure relaxes facial muscles and smooths out dynamic wrinkles caused by facial expressions.",
            "steps": [
                {
                    "title": "Consultation",
                    "desc": "Our experts assess your concerns and explain how Botox can help achieve your goals.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "Preparation",
                    "desc": "The treatment area is cleansed, and a numbing cream may be applied if needed.",
                    "icon": "fa-solid fa-prescription-bottle-medical"
                },
                {
                    "title": "Injections",
                    "desc": "Botox is carefully injected into targeted areas, taking about 10-20 minutes.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Aftercare",
                    "desc": "Resume daily activities but avoid rubbing the area or strenuous exercise for 4 hours.",
                    "icon": "fa-solid fa-hand-holding-medical"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of Allergan Botox",
            "subheading": "Therapeutic Benefits",
            "para": "Allergan Botox is a flexible treatment for both medical issues and cosmetic enhancement since it provides a variety of medicinal and aesthetic advantages.",
            "items": [
                {
                    "title": "Alleviates chronic migraines",
                    "desc": "By relaxing muscles and lowering headache frequency, Botox is an FDA-approved treatment for chronic migraines.",
                    "icon": "fa-solid fa-head-side-virus"
                },
                {
                    "title": "Treats excessive perspiration",
                    "desc": "Botox can reduce excessive perspiration in places like the underarms by blocking the chemical impulses that activate sweat glands.",
                    "icon": "fa-solid fa-droplet"
                },
                {
                    "title": "Reduces muscle spasms",
                    "desc": "Botox is frequently used to treat stiffness and spasms in the muscles, which helps with disorders including spasticity and cervical dystonia.",
                    "icon": "fa-solid fa-person-dots-from-line"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility for Allergan Botox",
            "subheading": "Ideal Applicants",
            "para": "Many people who want to address medical ailments or cosmetic concerns can benefit from Botox. However, a person's suitability as a candidate can be determined by a number of factors.",
            "items": [
                {
                    "title": "Age 18-65",
                    "desc": "People who wish to lessen the visibility of dynamic wrinkles and are between the ages of 18 and 65.",
                    "icon": "fa-solid fa-user-check"
                },
                {
                    "title": "Non-surgical seekers",
                    "desc": "People looking for non-surgical ways to seem younger.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "Medical condition patients",
                    "desc": "Botox can be used to treat patients with muscular problems, excessive perspiration, or recurrent migraines.",
                    "icon": "fa-solid fa-file-medical"
                },
                {
                    "title": "Preventive care",
                    "desc": "Those seeking anti-aging preventative measures.",
                    "icon": "fa-solid fa-shield-virus"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparing for the Botox Treatment",
            "subheading": "Essential Guidelines",
            "para": "It's critical to adequately prepare for your Botox treatment in order to guarantee the greatest outcomes and lower the possibility of side effects.",
            "items": [
                {
                    "title": "Avoid blood thinners",
                    "desc": "To reduce bruising, avoid taking aspirin, ibuprofen, or other blood-thinning drugs for at least a week before the procedure.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Limit alcohol",
                    "desc": "Because alcohol might raise the risk of bruises, it is advised to abstain from it for 24 hours before your treatment.",
                    "icon": "fa-solid fa-wine-glass-empty"
                },
                {
                    "title": "Medical disclosure",
                    "desc": "To make sure the procedure is safe for you, let your practitioner know about any allergies, medical problems, or drugs you are taking.",
                    "icon": "fa-solid fa-clipboard-user"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "What to Expect",
            "para": "Smooth, renewed skin is the result of Allergan Botox, giving you a more youthful appearance. Both cosmetic and therapeutic applications benefit greatly from the long-lasting and potent results.",
            "items": [
                {
                    "title": "Visible wrinkle reduction",
                    "desc": "After just one treatment, your skin will seem younger and smoother.",
                    "icon": "fa-solid fa-face-grin-stars"
                },
                {
                    "title": "Long-lasting effects",
                    "desc": "You can keep your face wrinkle-free and stop new wrinkles from appearing by getting regular treatments.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Boosted confidence",
                    "desc": "After receiving Botox treatments, many patients say they feel more assured and content with the way they look.",
                    "icon": "fa-solid fa-heart-circle-plus"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Aftercare Tips",
            "subheading": "Tips for Botox Full Face Aftercare: Get the Best Outcomes",
            "para": "Following your Botox full-face treatment, proper aftercare is essential to achieving the best possible results and avoiding any unintended side effects. There are still crucial precautions you should take to improve your experience and preserve your youthful appearance, even though the treatment is non-invasive and involves little to no downtime.",
            "items": [
                {
                    "title": "Do not rub or touch the treated areas",
                    "desc": "Avoid touching, rubbing, or massaging the regions that have received Botox for the first twenty-four hours following the procedure. This lessens the possibility that the Botox would spread to unwanted places, which could compromise your desired outcome.",
                    "icon": "fa-solid fa-hand-sparkles"
                },
                {
                    "title": "Remain upright for several hours",
                    "desc": "After treatment, it is advised that you stay upright for at least four hours. To guarantee that the Botox effectively settles into the targeted muscles, avoid lying down or bending over too much during this time.",
                    "icon": "fa-solid fa-person-walking"
                },
                {
                    "title": "24-hour break from intense exercise",
                    "desc": "For at least 24 hours following your treatment, refrain from intense exertion or other activity that creates excessive perspiration, even though mild activities like walking are acceptable. Blood flow can be increased by intense exercise, which could cause the Botox to move away from the treated areas.",
                    "icon": "fa-solid fa-dumbbell"
                },
                {
                    "title": "Avoid heat exposure",
                    "desc": "For the first 24 to 48 hours, avoid putting your face in the heat. Hot showers, saunas, steam rooms, and even direct sunshine fall under this category. Blood vessel dilatation brought on by heat may impair the effectiveness of Botox and your outcomes.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "No alcohol for 24 hours",
                    "desc": "Alcohol might cause blood thinning and raise the possibility of injection site bruises. Avoid drinking alcohol for at least 24 hours before and following your treatment to reduce the likelihood of bruises.",
                    "icon": "fa-solid fa-wine-glass-empty"
                },
                {
                    "title": "Avoid skin treatments and facials",
                    "desc": "For at least two weeks following your Botox operation, refrain from getting any more face treatments, such as chemical peels, microdermabrasion, or facials. These procedures might interfere with your results or interfere with the Botox.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "Gentle facial cleansing",
                    "desc": "After your treatment, you can wash your face, but be careful to wash it gently. Avoid using vigorous scrubbing motions near the injection sites and instead use lukewarm water and a mild cleaner.",
                    "icon": "fa-solid fa-pump-soap"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "If you are thinking of Botox Treatment in Dubai you must have some queries in mind about the procedure, the results, aftercare, etc. Below are some of the most common questions to assist you in making an informed decision.",
            "items": [
                {
                    "question": "What exactly is a full face Botox Treatment?",
                    "answer": "A Full Face Botox treatment uses small doses of Botulinum Toxin injections to various parts of the face to temporarily paralyze the muscles that contribute to formation of fine lines and wrinkles. Smiling or frowning several times can make our skin wrinkle, and this holistic approach can treat different zones of the face, including the forehead, frown lines, crow's feet, bunny lines (nose) and lip lines, to give a smoother and younger look. Botox is an excellent choice for the man who wants to minimize the appearance of age or to prevent wrinkle formation, but still have natural movements of the face. The effects usually last four to six months."
                },
                {
                    "question": "How many sessions would you need?",
                    "answer": "One session of Full Face Botox is often sufficient for noticeable results. Improvement from Botox usually takes about 3-5 days to show and reaches its peak effect at around 10–14 days. Because Botox is not permanent, most people require maintenance sessions every 4-6 months to maintain the results and continue enjoying smooth, wrinkle-free skin. How often you have sessions will depend on things that vary between each person like your skin type, age, and muscle movement. Your provider will recommend a personalized treatment plan during your consultation."
                },
                {
                    "question": "How long will the treatment process take?",
                    "answer": "SALON PROCEDURE: This procedure takes anywhere from 15-30minutes depending on which areas you are treating. It’s sometimes called a “lunchtime procedure” because it’s so fast and has so little downtime."
                },
                {
                    "question": "What are the potential side effects?",
                    "answer": "Common side effects are mild and include slight bruising, redness, or swelling at the injection sites. These usually subside within a few hours to a couple of days."
                },
                {
                    "question": "Are Botox Injections Painful?",
                    "answer": "Most patients feel little to no pain upon receiving Botox injections. The needles used are extremely fine, and the procedure is fast. An anesthetic cream may be applied to make treatment more comfortable if needed."
                },
                {
                    "question": "Who is an ideal candidate for Botox Full Face?",
                    "answer": "Botox Full Face is ideal for individuals looking to prevent or reduce the appearance of wrinkles and fine lines. Both men and women of various skin types can benefit from this treatment. During your consultation, we will assess your skin and aesthetic goals to ensure Botox is right for you."
                },
                {
                    "question": "What is the cost of Botox Full Face?",
                    "answer": "Botox Full Face cost varies from individual to individual based on the areas treated and required units. During your consultation, we will create a customized treatment plan with a cost breakdown."
                }
            ]
        },
        "treatmentAreas1": [
            {
                "ar": "خطوط الجبهة",
                "title": "Forehead Lines",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Forehead-Lines.jpg"
            },
            {
                "ar": "خطوط العبوس",
                "title": "Frown Lines",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Frown-Lines.jpg"
            },
            {
                "ar": "أقدام الغراب",
                "title": "Crow’s Feet",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Crows-Feet.jpg"
            }
        ],
        "treatmentAreas2": [
            {
                "ar": "خطوط الأرنب",
                "title": "Bunny Lines",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Bunny-Lines.jpg"
            },
            {
                "ar": "خطوط حول الشفاه",
                "title": "Lip Lines",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Lip-Lines.jpg"
            },
            {
                "ar": "تجاعيد الرقبة",
                "title": "Neck Bands",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Neck-Bandss.jpg"
            }
        ]

    },
    "shared": {
        "images": {
            "introductionImage": "",
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/ebh-services-uae-17-scaled.jpg",
            "beforeAfter": [
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Allergan-Botox-Treatment-in-Dubai-1.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Allergan-Botox-Treatment-in-Dubai-2.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Allergan-Botox-Treatment-in-Dubai-3.jpg"
            ]

        },
        "product": {
            "id": 1,
            "title": "Allergan Botox Treatment in Dubai",
            "description": "You may confidently improve your appearance and regain your glowing complexion with Allergan's well-established reputation and our individualized approach. Get to know a new, self-assured you now.",
            "price": 199,
        }
    },

    "ar": {
        "url": "allergan-botox",
        "meta": {
            "title": "علاج أليرجان بوتوكس في دبي | ايليت بودي هوم",
            "description": "تقليل التجاعيد بدون جراحة باستخدام أليرجان بوتوكس المعتمد من FDA. علاجات متخصصة لتجديد الشباب، علاج فرط التعرق، وتخفيف الصداع النصفي في دبي.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/allergan-botox",
            "og": {
                "title": "علاج أليرجان بوتوكس في دبي | ايليت بودي هوم",
                "description": "تقليل التجاعيد بدون جراحة باستخدام أليرجان بوتوكس المعتمد من FDA في دبي",
                "image": "https://www.elitebodyhome.com/images/allergan-botox-og.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "أليرجان بوتوكس"],
        "hero": {
            "title": "علاج أليرجان بوتوكس في دبي",
            "subtitle": "تجديد الوجه بدون جراحة",
            "description": "اكتشف التأثير المذهل للأليرجان بوتوكس في ايليت بودي هوم، حيث يلتقي العلم بالجمال. يتميز فريقنا المتخصص بتقديم نتائج طبيعية عن طريق التخلص من الخطوط الدقيقة والتجاعيد دون الحاجة إلى جراحة. يمكنك تحسين مظهرك واستعادة إشراقة بشرتك بثقة، مستفيدًا من سمعة أليرجان المتميزة ونهجنا المخصص وفقًا لاحتياجاتك. اكتشف نسخة أكثر ثقة من نفسك اليوم!"
        },
        "howItWorks": {
            "title": "ما هو أليرجان بوتوكس؟",
            "description": "يُعد بوتوكس أليرجان علاجًا موثوقًا ومعتمدًا من إدارة الغذاء والدواء الأمريكية (FDA)، مصممًا لتنعيم التجاعيد والخطوط الدقيقة. حيث يعمل البوتوكس على تقليل ظهور الخطوط التعبيرية وتجاعيد الجبهة وحول العينين من خلال إرخاء عضلات الوجه مؤقتًا، مما يمنحك مظهرًا أكثر شبابًا ونضارة."
        },
        "procedureSteps": {
            "heading": "خطوات إجراء أليرجان بوتوكس",
            "para": "يُعتبر أليرجان بوتوكس من أبرز الإجراءات التجميلية التي تعمل على تقليل التجاعيد والخطوط الدقيقة، مما يمنحك مظهرًا أكثر شبابًا وإشراقًا. يعتمد هذا الإجراء غير الجراحي على إرخاء عضلات الوجه لتنعيم التجاعيد الناتجة عن تعابير الوجه.",
            "steps": [
                {
                    "title": "الاستشارة",
                    "desc": "يقوم خبراؤنا بتقييم احتياجاتك وشرح كيفية تحقيق النتائج المرجوة من خلال البوتوكس.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "الاستعداد",
                    "desc": "تُنظف المنطقة المستهدفة، وقد يُستخدم كريم مخدر عند الحاجة.",
                    "icon": "fa-solid fa-prescription-bottle-medical"
                },
                {
                    "title": "الحقن",
                    "desc": "يُحقن البوتوكس بعناية في المناطق المستهدفة، وتستغرق الجلسة حوالي من 10 إالى 20 دقيقة.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "العناية بعد العلاج",
                    "desc": "يمكن استئناف الأنشطة اليومية، لكن يجب تجنب فرك المنطقة أو ممارسة التمارين المجهدة لمدة 4 ساعات.",
                    "icon": "fa-solid fa-hand-holding-medical"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد أليرجان بوتوكس",
            "subheading": "الفوائد العلاجية",
            "para": "يُعد بوتوكس أليرجان علاجًا متعدد الاستخدامات، حيث يوفر فوائد طبية وتجميلية تجعله خيارًا مثاليًا لتحسين المظهر وعلاج بعض الحالات الصحية.",
            "items": [
                {
                    "title": "تخفيف الصداع النصفي المزمن",
                    "desc": "يعمل البوتوكس على إرخاء العضلات وتقليل معدل تكرار نوبات الصداع، وهو علاج معتمد من إدارة الغذاء والدواء الأمريكية (FDA) لهذه الحالة.",
                    "icon": "fa-solid fa-head-side-virus"
                },
                {
                    "title": "علاج فرط التعرق",
                    "desc": "يساعد أليرجان بوتوكس على تقليل فرط التعرق في مناطق مثل الإبط، من خلال حجب الإشارات الكيميائية التي تحفز نشاط الغدد العرقية.",
                    "icon": "fa-solid fa-droplet"
                },
                {
                    "title": "تقليل التشنجات العضلية",
                    "desc": "يُستخدم البوتوكس لعلاج تيبس وتشنج العضلات، مما يساهم في التخفيف من أعراض بعض الاضطرابات مثل التشنج العضلي والتوتر العضلي العنقي.",
                    "icon": "fa-solid fa-person-dots-from-line"
                }
            ]
        },
        "eligibility": {
            "heading": "الأشخاص المؤهلون لاستخدام أليرجان بوتوكس",
            "subheading": "المرشحون المثاليون",
            "para": "يمكن أن يستفيد العديد من الأشخاص من علاج أليرجان بوتوكس سواء لأغراض تجميلية أو طبية، ولكن هناك بعض العوامل التي تحدد مدى ملاءمتهم للعلاج.",
            "items": [
                {
                    "title": "الأشخاص الذين تتراوح أعمارهم بين 18 و65 عامًا",
                    "desc": "الأشخاص الذين تتراوح أعمارهم بين 18 و65 عامًا ويرغبون في تقليل التجاعيد التعبيرية.",
                    "icon": "fa-solid fa-user-check"
                },
                {
                    "title": "الباحثون عن حلول غير جراحية",
                    "desc": "الأشخاص الذين يبحثون عن حلول غير جراحية للحصول على مظهر أكثر شبابًا.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "مرضى الحالات الطبية",
                    "desc": "المرضى الذين يعانون من اضطرابات عضلية، أو فرط التعرق، أو الصداع النصفي المتكرر.",
                    "icon": "fa-solid fa-file-medical"
                },
                {
                    "title": "الرعاية الوقائية",
                    "desc": "الأفراد الذين يرغبون في اتخاذ تدابير وقائية لمكافحة علامات التقدم في السن.",
                    "icon": "fa-solid fa-shield-virus"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد لإجراء أليرجان بوتوكس",
            "subheading": "إرشادات أساسية",
            "para": "يُعد التحضير الجيد قبل علاج البوتوكس خطوة أساسية لضمان تحقيق أفضل النتائج وتقليل الآثار الجانبية المحتملة.",
            "items": [
                {
                    "title": "تجنب مميعات الدم",
                    "desc": "امتنع عن تناول الأسبرين أو الإيبوبروفين أو الأدوية مميعة للدم لمدة لا تقل عن أسبوع قبل الجلسة.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "الحد من الكحول",
                    "desc": "ينصح بتجنب المشروبات الكحولية لمدة 24 ساعة قبل العلاج، حيث يمكن أن تزيد من احتمالية حدوث الكدمات.",
                    "icon": "fa-solid fa-wine-glass-empty"
                },
                {
                    "title": "الكشف عن التاريخ الطبي",
                    "desc": "أبلغ الطبيب إذا كنت تعاني من أي حساسية أو حالات طبية سابقة، أو إذا كنت تتناول أي أدوية، لضمان أمان الإجراء وفعاليته.",
                    "icon": "fa-solid fa-clipboard-user"
                }
            ]
        },
        "results": {
            "heading": "النتائج المتوقعة",
            "subheading": "ما يمكن توقعه",
            "para": "يمنحك بوتوكس أليرجان بشرة أكثر نعومة ونضارة، مما يعزز مظهرك الشبابي. سواء كان الاستخدام تجميليًا أو علاجيًا، فإن نتائجه قوية وطويلة الأمد.",
            "items": [
                {
                    "title": "تقليل واضح للتجاعيد",
                    "desc": "ستبدو البشرة أكثر نعومة وحيوية بعد جلسة واحدة فقط.",
                    "icon": "fa-solid fa-face-grin-stars"
                },
                {
                    "title": "آثار طويلة الأمد",
                    "desc": "يساهم العلاج المنتظم في الحفاظ على مظهر خالي من التجاعيد ويمنع ظهور تجاعيد جديدة.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "تعزيز الثقة بالنفس",
                    "desc": "يشعر العديد من المرضى بعد العلاج بمزيد من الثقة والرضا عن مظهرهم.",
                    "icon": "fa-solid fa-heart-circle-plus"
                }
            ]
        },
        "aftercareTips": {
            "heading": "نصائح العناية بعد العلاج",
            "subheading": "نصائح للعناية بعد البوتوكس: تحقيق أفضل النتائج",
            "para": "يجب اتباع إرشادات العناية بعد العلاج لضمان أفضل النتائج وتجنب الآثار الجانبية غير المرغوبة. رغم أن البوتوكس علاج غير جراحي ولا يتطلب فترة تعافي طويلة، إلا أن هناك بعض الاحتياطات التي يجب اتخاذها للحفاظ على مظهر شبابي يدوم لفترة أطول.",
            "items": [
                {
                    "title": "عدم لمس المناطق المعالجة",
                    "desc": "تجنب لمس أو فرك أو تدليك المنطقة المعالجة خلال أول 24 ساعة بعد العلاج، لتجنب انتشار البوتوكس إلى مناطق غير المستهدفة مما قد يؤثر على النتائج.",
                    "icon": "fa-solid fa-hand-sparkles"
                },
                {
                    "title": "البقاء في وضع مستقيم",
                    "desc": "ينصح بالبقاء في وضع مستقيم لمدة لا تقل عن 4 ساعات بعد العلاج، وتجنب الاستلقاء أو الانحناء الشديد لضمان استقرار البوتوكس في العضلات المستهدفة.",
                    "icon": "fa-solid fa-person-walking"
                },
                {
                    "title": "تجنب التمارين الشاقة لمدة 24 ساعة",
                    "desc": "تجنب النشاط البدني المكثف والتعرق الزائد لمدة 24 ساعة بعد العلاج، حيث يمكن أن يؤثر تدفق الدم المتزايد على فعالية العلاج.",
                    "icon": "fa-solid fa-dumbbell"
                },
                {
                    "title": "تجنب التعرض للحرارة",
                    "desc": "تجنب التعرض للحرارة العالية مثل الحمامات الساخنة، والساونا، وغرف البخار خلال أول 48 ساعة، حيث يمكن أن تؤثر الحرارة على نتائج العلاج.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "تجنب الكحول لمدة 24 ساعة",
                    "desc": "الامتناع عن المشروبات الكحولية لمدة 24 ساعة قبل وبعد العلاج، حيث يمكن أن تؤدي إلى سيولة الدم وزيادة خطر الكدمات في مناطق الحقن.",
                    "icon": "fa-solid fa-wine-glass-empty"
                },
                {
                    "title": "تجنب علاجات البشرة الأخرى",
                    "desc": "ابتعد عن جلسات تنظيف البشرة العميقة، أو التقشير الكيميائي أو الدقيق (المايكرودرمابريجن) لمدة أسبوعين على الأقل بعد جلسة البوتوكس، حيث يمكن أن تتداخل مع النتائج.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "تنظيف الوجه بلطف",
                    "desc": "يمكنك غسل وجهك بعد العلاج، ولكن استخدم ماء فاتر وغسول لطيف، مع تجنب الفرك الشديد في مناطق الحقن.",
                    "icon": "fa-solid fa-pump-soap"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "إذا كنت تفكر في علاج البوتوكس في دبي، فقد يكون لديك بعض الاستفسارات حول الإجراء، النتائج، العناية بعد العلاج، وغير ذلك. فيما يلي بعض الأسئلة الشائعة لمساعدتك في اتخاذ قرار مستنير.",
            "items": [
                {
                    "question": "ما هو علاج البوتوكس للوجه بالكامل؟",
                    "answer": "يستخدم علاج البوتوكس للوجه بالكامل جرعات صغيرة من حقن البوتولينوم توكسين في أجزاء مختلفة من الوجه لشل العضلات المسببة للتجاعيد والخطوط الدقيقة مؤقتًا. يمكن أن يؤدي الابتسام أو العبوس المتكرر إلى تجعد بشرتنا، وهذا النهج الشامل يعالج مناطق مختلفة من الوجه بما في ذلك الجبهة، خطوط العبوس، تجاعيد العين، خطوط الأرنب (الأنف) وخطوط الشفاه، لمنحك مظهرًا أكثر نعومة وشبابًا. يعتبر البوتوكس خيارًا ممتازًا للرجل الذي يرغب في تقليل مظاهر الشيخوخة أو منع تكون التجاعيد، مع الحفاظ على تعابير الوجه الطبيعية. تستمر النتائج عادة من أربعة إلى ستة أشهر."
                },
                {
                    "question": "كم عدد الجلسات التي سأحتاجها؟",
                    "answer": "عادة ما تكون جلسة واحدة من البوتوكس للوجه بالكامل كافية لملاحظة النتائج. يبدأ التحسن من البوتوكس في الظهور بعد حوالي 3-5 أيام ويصل إلى ذروته بعد حوالي 10-14 يومًا. نظرًا لأن البوتوكس ليس دائمًا، فإن معظم الناس يحتاجون إلى جلسات صيانة كل 4-6 أشهر للحفاظ على النتائج والاستمتاع ببشرة ناعمة خالية من التجاعيد. يعتمد عدد الجلسات التي تحتاجها على عوامل تختلف من شخص لآخر مثل نوع بشرتك، عمرك، وحركة عضلاتك. سيوصي مقدم الخدمة بخطة علاج مخصصة خلال استشارتك."
                },
                {
                    "question": "كم من الوقت تستغرق عملية العلاج؟",
                    "answer": "الإجراء: تستغرق هذه العملية من 15 إلى 30 دقيقة حسب المناطق التي يتم علاجها. يُطلق عليها أحيانًا اسم \"إجراء وقت الغداء\" لأنها سريعة جدًا وتتطلب وقتًا قصيرًا للتعافي."
                },
                {
                    "question": "ما هي الآثار الجانبية المحتملة؟",
                    "answer": "الآثار الجانبية الشائعة خفيفة وتشمل كدمات طفيفة، احمرار، أو تورم في مواقع الحقن. عادة ما تختفي هذه الأعراض في غضون بضع ساعات إلى يومين."
                },
                {
                    "question": "هل حقن البوتوكس مؤلمة؟",
                    "answer": "يشعر معظم المرضى بألم قليل أو عدم وجود ألم عند تلقي حقن البوتوكس. الإبر المستخدمة دقيقة جدًا، والإجراء سريع. يمكن تطبيق كريم مخدر إذا لزم الأمر لجعل العلاج أكثر راحة."
                },
                {
                    "question": "من هو المرشح المثالي للبوتوكس للوجه بالكامل؟",
                    "answer": "البوتوكس للوجه بالكامل مثالي للأفراد الذين يرغبون في منع أو تقليل ظهور التجاعيد والخطوط الدقيقة. يمكن أن يستفيد كل من الرجال والنساء من مختلف أنواع البشرة من هذا العلاج. خلال استشارتك، سنقيم بشرتك وأهدافك الجمالية لضمان أن البوتوكس مناسب لك."
                },
                {
                    "question": "ما هي تكلفة البوتوكس للوجه بالكامل؟",
                    "answer": "تختلف تكلفة البوتوكس للوجه بالكامل من شخص لآخر بناءً على المناطق المعالجة وعدد الوحدات المطلوبة. خلال استشارتك، سنقوم بإنشاء خطة علاج مخصصة مع تفصيل للتكلفة."
                }
            ]
        },
        "treatmentAreas1": [
            {
                "ar": "خطوط الجبهة",
                "title": "خطوط الجبهة",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Forehead-Lines.jpg"
            },
            {
                "ar": "خطوط العبوس",
                "title": "خطوط العبوس",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Frown-Lines.jpg"
            },
            {
                "ar": "أقدام الغراب",
                "title": "أقدام الغراب",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Crows-Feet.jpg"
            }
        ],
        "treatmentAreas2": [
            {
                "ar": "خطوط الأرنب",
                "title": "خطوط الأرنب",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Bunny-Lines.jpg"
            },
            {
                "ar": "خطوط حول الشفاه",
                "title": "خطوط حول الشفاه",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Lip-Lines.jpg"
            },
            {
                "ar": "تجاعيد الرقبة",
                "title": "تجاعيد الرقبة",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Neck-Bandss.jpg"
            }
        ]
    }

};
